import classNames from 'classnames';
import { useMemo, useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import Button from 'src/components/Button';
import PageLayout from 'src/components/layouts/PageLayout';
import showAlert from 'src/utils/showAlert';
import useWindowWidth from 'src/utils/hooks/useWindowWidth';
import { axiosDefault, Error } from 'src/utils/axios-helper';
import { commonNamespaces } from 'src/utils/setUpTranslation';
import { DailySchedule, ScheduleLesson } from 'src/interfaces/lesson';
import { renderPage } from 'src/utils/PageTemplate';
import { showReservationConfirm } from 'src/components/dialogs/ReservationConfirm';

import styles from './Schedule.module.scss';

interface Props {
  schedule: DailySchedule[];
  has_next: boolean;
}

const SchedulePage = ({ schedule, has_next: hasNext }: Props) => {
  const { t } = useTranslation('schedule');
  const isFetching = useRef(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const [curIndex, setCurIndex] = useState(0);
  const [data, setData] = useState(schedule ? [{ schedule, hasNext }] : []);
  const [selected, setSelected] = useState<{ date: string; lesson: ScheduleLesson } | null>(null);
  const weekdayMap = useMemo(() => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], []);
  const windowWidth = useWindowWidth();
  const [lessonHeight, lessonMargin] = useMemo(() => {
    if (windowWidth > 1080) return [120, 12];
    return [96, 10];
  }, [windowWidth]);

  const handleNextWeek = async () => {
    if (curIndex + 1 < data.length) setCurIndex(curIndex + 1);
    else {
      if (isFetching.current) return;
      try {
        const res = await axiosDefault.get<{ status: string; data: Props }>('/class/api/weekly-schedule', {
          params: { week: curIndex + 1 },
        });
        if (res.status === 200 && res.data.status === 'ok') {
          const { schedule, has_next: hasNext } = res.data.data;
          setData([...data, { schedule, hasNext }]);
          setCurIndex(curIndex + 1);
        }
      } catch (error) {
        console.error(error);
        showAlert({ message: 'Failed to fetch schedule' });
      } finally {
        isFetching.current = false;
      }
    }
  };

  const handleReservation = async () => {
    if (!selected) return;
    isFetching.current = true;
    const lessonName = selected.lesson.name;
    try {
      const res = await axiosDefault.post('/class/api/reservation', { lesson: lessonName });
      if (res.status === 200) {
        showAlert({ message: t('booking-success') });
        setSelected(null);
      }
    } catch (err) {
      console.log(err);
      const error = err as Error;
      if (error.response?.data.message) {
        const errorCode = error.response.data.code;
        const callback = errorCode === 4 ? () => window.location.assign('/user') : undefined;
        showAlert({ message: error.response.data.message, callback });
      } else {
        const statusCode = error.response?.status;
        showAlert({ message: `Failed to reserve lesson ${statusCode}` });
      }
    } finally {
      isFetching.current = false;
    }
  };

  const handleReservationClick = () => {
    if (!selected) return;
    showReservationConfirm({
      type: 'reservation',
      coach: selected.lesson.coach,
      date: selected.date,
      startTime: selected.lesson.time.start,
      endTime: selected.lesson.time.end,
      onConfirm: handleReservation,
    });
  };

  useEffect(() => {
    if (tableRef.current) tableRef.current.scrollLeft = 0;
  }, [curIndex]);

  return (
    <>
      <Helmet>
        <title>{t('schedule')}</title>
      </Helmet>
      <PageLayout>
        <div className={styles.header}>
          <div className={styles.left}>
            <h1 className={styles.title}>{t('courses')}</h1>
            <p className={styles.info}>{t('courses-info')}</p>
          </div>
          <div className={styles.right}>
            {selected && (
              <div className={styles.selected} style={{ background: selected.lesson.coach_color }}>
                <div>
                  <div>{selected.lesson.coach}</div>
                  <div>
                    {selected.lesson.time.start}-{selected.lesson.time.end}
                  </div>
                </div>
                <div className={styles.desc}>{selected.lesson.description}</div>
              </div>
            )}
            <Button
              disabled={!selected}
              className={styles.button}
              buttonType="button"
              type="primary"
              onClick={handleReservationClick}
            >
              {t('booking')}
            </Button>
          </div>
        </div>
        <div className={styles.controls}>
          <Button
            className={styles.button}
            buttonType="button"
            type="default"
            disabled={curIndex === 0}
            onClick={() => setCurIndex(curIndex - 1)}
          >
            {t('prev-week')}
          </Button>
          <Button
            className={styles.button}
            buttonType="button"
            type="default"
            disabled={!data[curIndex]?.hasNext}
            onClick={handleNextWeek}
          >
            {t('next-week')}
          </Button>
        </div>
        {data.length > 0 ? (
          <div className={styles.table} ref={tableRef}>
            {data[curIndex]?.schedule?.map(({ date, lessons }, idx) => (
              <div key={date} className={styles.row}>
                <div className={styles.date}>
                  <div>{weekdayMap[idx]}</div>
                  <div>{date}</div>
                </div>
                <div className={styles.lessons}>
                  {lessons
                    .filter(({ time }) => !!time)
                    .map((lesson) => {
                      const { coach, coach_color: color, description, time, time_position: position } = lesson;
                      const background = color || '#000000';
                      const white = background === '#000000';
                      return (
                        <button
                          disabled={!lesson.available}
                          onClick={() => setSelected({ date, lesson })}
                          className={classNames(styles.lesson, {
                            [styles.white]: white,
                            [styles.selected]: selected?.lesson === lesson,
                          })}
                          style={{ background, top: position * (lessonHeight + lessonMargin) }}
                        >
                          <div>
                            <div>{coach}</div>
                            <div>
                              {time.start}-{time.end}
                            </div>
                          </div>
                          <div className={styles.desc}>{description}</div>
                        </button>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>Error</div>
        )}
      </PageLayout>
    </>
  );
};

renderPage(SchedulePage, [...commonNamespaces, 'schedule']);

export default SchedulePage;
